import _ from 'lodash';
import Formatter from '../utils/Formatter';
import Mapper from '../utils/Mapper';
import {DocumentModels, kycTypeValues, EformAvailableAccountTypes} from '../constants/IDVerificationConstants.js';
import YourDetailsConstants from '../constants/YourDetailsConstants';
import Products from '../utils/Products.js';

function convertToMetaDoc(verificationDocuments, investorId, isRetreived) {
    if (!verificationDocuments) {
        return [];
    }

    const defaultMetaDoc = {
        documentType: null,
        id: null,
        investorId: null,
        isEditing: false,
        isMain: false,
        isValid: isRetreived,
        reactKey: null,
        sortIndex: null,
        type: null,
        typeDescription: null,
        meta: []
    }
    
    let metaDocs = [];
    _.each(verificationDocuments, (doc, docIdx) => {
        const docDefault = _.find(DocumentModels, {reactKey: doc.id});
        let metaDoc = _.cloneDeep(defaultMetaDoc);
        metaDoc.documentType = docDefault.documentType;
        metaDoc.id = 'investor' + investorId + '_' + docIdx;
        metaDoc.isMain = docDefault.isMain;
        metaDoc.reactKey = docDefault.reactKey;
        metaDoc.sortIndex = docDefault.sortIndex;
        metaDoc.type = docDefault.type;
        metaDoc.typeDescription = docDefault.typeDescription;
        metaDoc.meta = _.cloneDeep(docDefault.meta);

        _.each(metaDoc.meta, field => {
            if (_.has(doc, field.id)) {
                field.value = doc[field.id];
            }
        })
        metaDocs.push(metaDoc);
    });
    return metaDocs;
}

function getUpdatedInvestorDetails(yourDetails, titleList, idVerificationModel) {
    // this method is to update the investor full name in case adviser comes back and fro and change name on the app form
    if (!yourDetails) return null;
    if (!idVerificationModel) return null;

    let model = _.cloneDeep(idVerificationModel);

    let investorList = yourDetails.investorList && yourDetails.investorList.length > 0
       ? _.cloneDeep(yourDetails.investorList)
       :[_.cloneDeep(yourDetails)];

    if (investorList.length !== model.investorList.length) {
        // length change caused by account type change, reset id verification documents entered.
        return null;
    }

    _.each(investorList, (investor, idx) => {
        let modelToBeUpdated = _.find(model.investorList, { investorId: idx });
        const investorTitle = Mapper.getGenderTitle(investor.gender, investor.title, titleList,
                YourDetailsConstants.Values.GENDER_MALE, YourDetailsConstants.Values.GENDER_FEMALE);
        const fullName = Formatter.formatFullname(investorTitle, investor.givenName, investor.lastName);
        if (modelToBeUpdated) {
            modelToBeUpdated.fullName = fullName;
            modelToBeUpdated.kycFlag = investor.kycFlag;
            modelToBeUpdated.kycType = investor.kycType;
            modelToBeUpdated.oin = investor.oin;
        }
    });

    return model;
}

function flattenVerificationDocuments(formModel, titleList, isRetrieved) {
    const defaultInvestorModel = {
        verificationDocuments: [],
        oin: null,
        fullName: null,
        investorId: null,
        hasRequiredDocs: false,
        hasNonPrimaryOnly: false,
        isValid: false,
        kycFlag: false,
        kycType: null
    };

    let flattendIdVerificationModel = { investorList: [] };
    let investorList = formModel.form.yourDetails.investorList && formModel.form.yourDetails.investorList.length > 0
        ? _.cloneDeep(formModel.form.yourDetails.investorList)
        :[_.cloneDeep(formModel.form.yourDetails)];

    _.each(investorList, function(investor, investorId) {
        let investorModel = _.cloneDeep(defaultInvestorModel);
        const investorTitle = Mapper.getGenderTitle(investor.gender, investor.title, titleList,
                YourDetailsConstants.Values.GENDER_MALE, YourDetailsConstants.Values.GENDER_FEMALE);
        const fullName = Formatter.formatFullname(investorTitle, investor.givenName, investor.lastName);
        _.assign(investorModel,
        {
            investorId: investorId,
            fullName: fullName,
            kycFlag: investor.kycFlag,
            kycType: investor.kycType,
            oin: investor.oin,
            verificationDocuments: convertToMetaDoc(investor.verificationDocuments, investorId, isRetrieved)
        });
        flattendIdVerificationModel.investorList.push(investorModel);
    });

    return flattendIdVerificationModel;
}

function formatVerificationDocuments(productId, accountTypeCode, yourDetails, idVerificationModel, updateKyc=false) {
    // this method is called to format _formModel.form.yourDetails before it is sent back to server side
    if (!yourDetails) return yourDetails;
    if (!idVerificationModel) return yourDetails;
    if (!productId) return yourDetails;
    if (!accountTypeCode) return yourDetails;

    const isEFormRequired = (Products.isInvestment(productId) && _.includes(EformAvailableAccountTypes, accountTypeCode)) ||
        Products.isSuper(productId) || Products.isPension(productId);
    if (!isEFormRequired) return yourDetails;

    const defaultVerificationDocument = {
        docId: null,
        id: null,
        type: null,
        typeDescription: null,
        issuer: null,
        issueState: null,
        number: null,
        nameOnDocument: null,
        issuedDate: null,
        expiryDate: null,
        verifiedFrom: null,
        dateVerified: null,
        translation: null
    }

    const investorList = _.cloneDeep(idVerificationModel.investorList);
    const singleInvestor = investorList.length === 1;

    let result = _.cloneDeep(yourDetails);
    _.each(investorList, investor => {
        let verificationDocuments = [];
        _.each(investor.verificationDocuments, doc => {
            let docModel = _.clone(defaultVerificationDocument);
            docModel.docId = doc.id;
            docModel.id = doc.reactKey;
            docModel.type = doc.type;
            docModel.typeDescription = doc.typeDescription;
            _.each(doc.meta, metaField => {
                if (_.has(docModel, metaField.id)) {
                    docModel[metaField.id] = metaField.value;
                }
            });

            verificationDocuments.push(docModel);
        });

        if (singleInvestor) {
            if (updateKyc) {
                let newKycFlag = investor.isValid;
                _.assign(result,
                {
                    verificationDocuments: verificationDocuments, 
                    eformCompleted: newKycFlag
                });
            } else {
                _.assign(result, { verificationDocuments: verificationDocuments });
            }
            
        } else {
            if (result.investorList[investor.investorId]) {
                if (updateKyc) {
                    let newKycFlag = investor.isValid;
                    _.assign(result.investorList[investor.investorId], { verificationDocuments: verificationDocuments, 
                        eformCompleted: newKycFlag
                    });
                } else {
                    _.assign(result.investorList[investor.investorId], { verificationDocuments: verificationDocuments});
                }
            }
        }
    });
    return result;
}

function verifyKycUpdate(application) {
    if (!application) return null;
    let updatedApplication = _.cloneDeep(application);
    const eFormKycType = kycTypeValues.E_FORM;
    const commSeeKycType = kycTypeValues.COMM_SEE;

    const isCommSee = updatedApplication.form.yourDetails.kycTypeToUpdate === commSeeKycType;
    if (updatedApplication.form.yourDetails.investorList.length === 0) {
        if (updatedApplication.form.yourDetails.kycFlag && isCommSee) {
            _.assign(updatedApplication.form.yourDetails, { kycFlag: true, kycType: commSeeKycType });
        }else if (!updatedApplication.form.yourDetails.kycFlag && updatedApplication.form.yourDetails.eformCompleted) {
            _.assign(updatedApplication.form.yourDetails, { kycFlag: true, kycType: eFormKycType });
        }
    } else {
        _.each(updatedApplication.form.yourDetails.investorList, investor => {
            if (investor.kycFlag && isCommSee) {
                _.assign(investor, { kycFlag: true, kycType: commSeeKycType });
            }else if (!investor.kycFlag && investor.eformCompleted) {
                _.assign(investor, { kycFlag: true, kycType: eFormKycType });
            }
        });
    }
    return updatedApplication;
}

function getUpdateFormModelWithKycTypeToUpdate(formModel, kycTypeToUpdate) {
    if (formModel.form && formModel.form.yourDetails) {
        formModel.form.yourDetails.kycTypeToUpdate = kycTypeToUpdate;
    }
    return formModel;
}

function clearKycInfoForNonCfpAdviser(application, titleList) {
    if (!application) return null;
    let updatedApplication = _.cloneDeep(application);

    if (updatedApplication.form.yourDetails.investorList.length === 0) {
        _.assign(updatedApplication.form.yourDetails, { kycFlag: false });
    } else {
        _.each(updatedApplication.form.yourDetails.investorList, investor => {
            _.assign(investor, { kycFlag: false });
        });
    }

    const defaultInvestorModel = {
        verificationDocuments: [],
        oin: null,
        fullName: null,
        investorId: null,
        hasRequiredDocs: false,
        hasNonPrimaryOnly: false,
        isValid: false,
        kycFlag: false,
        kycType: null
    };

    let flattendIdVerificationModel = { investorList: [] };
    let investorList = updatedApplication.form.yourDetails.investorList && updatedApplication.form.yourDetails.investorList.length > 0
        ? _.cloneDeep(updatedApplication.form.yourDetails.investorList)
        :[_.cloneDeep(updatedApplication.form.yourDetails)];

    _.each(investorList, function(investor, investorId) {
        let investorModel = _.cloneDeep(defaultInvestorModel);
        const investorTitle = Mapper.getGenderTitle(investor.gender, investor.title, titleList,
            YourDetailsConstants.Values.GENDER_MALE, YourDetailsConstants.Values.GENDER_FEMALE);
        const fullName = Formatter.formatFullname(investorTitle, investor.givenName, investor.lastName);
        _.assign(investorModel,
            {
                investorId: investorId,
                fullName: fullName,
                kycFlag: investor.kycFlag,
                kycType: investor.kycType,
                oin: investor.oin,
                verificationDocuments: convertToMetaDoc(investor.verificationDocuments, investorId, true)
            });
        flattendIdVerificationModel.investorList.push(investorModel);
    });
    updatedApplication.form.idVerification = flattendIdVerificationModel;
    return updatedApplication;
}


const IdVerificationHelper = {
    flattenVerificationDocuments: flattenVerificationDocuments, //to get a list if verification documents from investorList
    formatVerificationDocuments: formatVerificationDocuments, //to format meta data to a proper model for submit and save
    verifyKycUpdate: verifyKycUpdate,
    getUpdatedInvestorDetails: getUpdatedInvestorDetails,
    getUpdateFormModelWithKycTypeToUpdate: getUpdateFormModelWithKycTypeToUpdate,
    clearKycInfoForNonCfpAdviser: clearKycInfoForNonCfpAdviser
};

export default IdVerificationHelper;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./utils/IdVerificationHelper.js